// src/components/OptimizedPicture/OptimizedPicture.jsx
import React from "react";
import { getOptimizedSources, buildSrcSets } from "../../utils/getOptimizedSources";

/**
 * Use like:
 * <OptimizedPicture
 *   src="assets/banner-bg2.jpg"
 *   alt="TWIG hero"
 *   sizes="(max-width: 768px) 100vw, 1200px"
 *   width={1200}
 *   height={600}
 *   priority
 * />
 */
export default function OptimizedPicture({
  src,
  alt = "",
  sizes = "100vw",
  className,
  width,
  height,
  priority = false,
  style
}) {
  const item = getOptimizedSources(src);
  const loading = priority ? "eager" : "lazy";
  const decoding = priority ? "sync" : "async";
  const fetchpriority = priority ? "high" : "auto";

  // If not found in manifest (e.g., during dev before first build), fall back to original
  if (!item.base) {
    // Assumes CRA asset import path in /src works via <img src={require(...)}>, but for simplicity:
    return (
      <img
        src={src.replace(/^src\//, "/")}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        fetchpriority={fetchpriority}
        width={width}
        height={height}
        style={style}
      />
    );
  }

  const avifSet = buildSrcSets(item.base, item.sizes, "avif");
  const webpSet = buildSrcSets(item.base, item.sizes, "webp");
  // Pick the largest fallback as default src
  const largest = item.sizes[item.sizes.length - 1];
  const fallbackExt = src.toLowerCase().endsWith(".png") ? "png" : "jpg";
  const fallbackSrc = `${item.base}-${largest}.${fallbackExt}`;
  const fallbackSet = buildSrcSets(item.base, item.sizes, fallbackExt);

  return (
    <picture>
      <source type="image/avif" srcSet={avifSet} sizes={sizes} />
      <source type="image/webp" srcSet={webpSet} sizes={sizes} />
      <img
        src={fallbackSrc}
        srcSet={fallbackSet}
        sizes={sizes}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        fetchpriority={fetchpriority}
        width={width}
        height={height}
        style={style}
      />
    </picture>
  );
}
